import React, { useState, useMemo, useContext, useCallback } from 'react';
import { useWorkloadSummary } from '../api/activityApi';
import { useQueryClient } from '@tanstack/react-query';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import { FiUsers, FiBriefcase, FiAlertCircle, FiCheckCircle, FiTrendingUp, FiActivity, FiLayers, FiFilter, FiSearch, FiUser, FiClock, FiRefreshCw, FiFileText, FiCopy, FiBookOpen } from 'react-icons/fi';
import TeamSopManageDialog from '../components/TeamSopManageDialog';
import EmployeeProfileModal from '../components/EmployeeProfileModal';
import { AuthContext } from '../auth/AuthContext';
import { generateWeeklyTeamReport } from '../api/aiApi';
import toast from 'react-hot-toast';
import { canSetupPerformanceReviewCycle, PerformanceReviewCycleMissingBanner } from '../components/PerformanceReviewCycleSetup';
import { COLORS as C, RADIUS } from '../constants/ui';

// Helper function to format date as DD/MM/YYYY
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Workload level colors
const WORKLOAD_COLORS = {
  low: { bg: C.doneSoft, color: '#166534', label: 'Light' },
  medium: { bg: '#fef9c3', color: '#854d0e', label: 'Moderate' },
  high: { bg: C.dangerSoft, color: '#991b1b', label: 'Heavy' }
};

// Role display names
const ROLE_DISPLAY = {
  admin: 'Admin',
  superadmin: 'Super Admin',
  manager: 'Manager',
  product_manager: 'Product Manager',
  developer: 'Developer',
  tester: 'QA Tester',
  designer: 'Designer',
  devops: 'DevOps',
  analyst: 'Analyst',
  lead: 'Team Lead',
  member: 'Member',
  marketing_manager: 'Marketing Manager',
  brand_manager: 'Brand Manager',
  pr_manager: 'PR Manager',
};

const WEEKLY_REPORT_ROLES = ['admin', 'superadmin', 'manager', 'analyst', 'lead', 'product', 'product_manager'];

const DOCUMENTS_MANAGE_ROLES = ['admin', 'superadmin', 'manager'];

/**
 * Team Activity page title: managers / product / BA-style roles see “Performance Review” framing;
 * admin & superadmin keep the workload dashboard title (they still open anyone’s profile, including managers, for formal review + comments).
 */
const TEAM_ACTIVITY_PR_TITLE_ROLES = new Set([
  'manager',
  'marketing_manager',
  'brand_manager',
  'product_manager',
  'product',
  'analyst',
  'lead',
]);

function teamActivityPageCopy(viewerRole) {
  const r = String(viewerRole || '').toLowerCase();
  if (TEAM_ACTIVITY_PR_TITLE_ROLES.has(r)) {
    return {
      title: 'Performance Review',
      subtitle:
        'Review how work is spread across the team—open a member to discuss workload and add formal review comments for the current cycle.',
    };
  }
  return {
    title: 'Team Workload & Performance',
    subtitle: 'Monitor team capacity, workload distribution, and performance across all employees',
  };
}

/** Explicit modal phases so we never infer UI from loading+text alone (avoids stale state / skipped config). */
const WR_CONFIGURE = 'configure';
const WR_GENERATING = 'generating';
const WR_RESULT = 'result';

const TeamActivity = () => {
  const { role } = useContext(AuthContext);
  const qc = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [workloadFilter, setWorkloadFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [profileUser, setProfileUser] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [weeklyReportOpen, setWeeklyReportOpen] = useState(false);
  const [weeklyReportLoading, setWeeklyReportLoading] = useState(false);
  const [weeklyReportText, setWeeklyReportText] = useState('');
  const [weeklyReportMeta, setWeeklyReportMeta] = useState(null);
  const [weeklySummaryMode, setWeeklySummaryMode] = useState('project');
  const [weeklyReportPeriod, setWeeklyReportPeriod] = useState('both');
  const [weeklyReportPhase, setWeeklyReportPhase] = useState(WR_CONFIGURE);
  const [documentsManageOpen, setDocumentsManageOpen] = useState(false);
  const canGenerateWeeklyReport = WEEKLY_REPORT_ROLES.includes(String(role || '').toLowerCase());
  const canManageDocuments = DOCUMENTS_MANAGE_ROLES.includes(String(role || '').toLowerCase());
  const weeklyReportBusy = weeklyReportPhase === WR_GENERATING;
  const teamPageCopy = useMemo(() => teamActivityPageCopy(role), [role]);

  const { data: employees = [], isLoading, isError, error, isFetching } = useWorkloadSummary();

  const handleRefresh = async () => {
    setRefreshing(true);
    await qc.invalidateQueries({ queryKey: ['workload-summary'] });
    setTimeout(() => setRefreshing(false), 600);
  };

  const openWeeklyReportModal = useCallback(() => {
    setWeeklyReportOpen(true);
    setWeeklyReportPhase(WR_CONFIGURE);
    setWeeklyReportLoading(false);
    setWeeklyReportText('');
    setWeeklyReportMeta(null);
  }, []);

  const resetWeeklyReportContent = useCallback(() => {
    setWeeklyReportText('');
    setWeeklyReportMeta(null);
    setWeeklyReportLoading(false);
    setWeeklyReportPhase(WR_CONFIGURE);
  }, []);

  const runWeeklyReportGeneration = useCallback(async () => {
    setWeeklyReportPhase(WR_GENERATING);
    setWeeklyReportLoading(true);
    setWeeklyReportText('');
    setWeeklyReportMeta(null);
    try {
      const projectName = projectFilter === 'all' ? null : projectFilter;
      const { data } = await generateWeeklyTeamReport({
        projectName,
        summaryMode: weeklySummaryMode,
        reportPeriod: weeklyReportPeriod,
      });
      setWeeklyReportText(data?.report || '');
      setWeeklyReportMeta(data?.meta || null);
      setWeeklyReportPhase(WR_RESULT);
      if (!data?.report) toast.error('No report text returned');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to generate report';
      toast.error(msg);
      setWeeklyReportPhase(WR_CONFIGURE);
    } finally {
      setWeeklyReportLoading(false);
    }
  }, [projectFilter, weeklySummaryMode, weeklyReportPeriod]);

  const handleCopyWeeklyReport = useCallback(async () => {
    if (!weeklyReportText) return;
    try {
      await navigator.clipboard.writeText(weeklyReportText);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Could not copy — select the text and copy manually');
    }
  }, [weeklyReportText]);

  // Filter employees based on search and filters
  const filteredEmployees = useMemo(() => {
    return employees.filter(emp => {
      const matchesSearch = !searchTerm || 
        (emp.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.team || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesRole = roleFilter === 'all' || emp.role === roleFilter;
      const matchesWorkload = workloadFilter === 'all' || emp.workload?.workload_level === workloadFilter;
      const matchesProject = projectFilter === 'all' || (emp.projects || []).includes(projectFilter);
      
      return matchesSearch && matchesRole && matchesWorkload && matchesProject;
    });
  }, [employees, searchTerm, roleFilter, workloadFilter, projectFilter]);

  // Calculate team stats — reflect current filters
  const teamStats = useMemo(() => {
    const total = filteredEmployees.length;
    const withActiveWork = filteredEmployees.filter(e => (e.workload?.total_active || 0) > 0).length;
    const overloaded = filteredEmployees.filter(e => e.workload?.workload_level === 'high').length;
    const totalBlockers = filteredEmployees.reduce((sum, e) => sum + (e.workload?.blockers || 0), 0);
    const totalCompleted = filteredEmployees.reduce((sum, e) => sum + (e.workload?.completed_tasks || 0), 0);
    const avgTasksPerPerson = total > 0 ? Math.round(filteredEmployees.reduce((sum, e) => sum + (e.workload?.total_active || 0), 0) / total) : 0;
    
    return { total, withActiveWork, overloaded, totalBlockers, totalCompleted, avgTasksPerPerson };
  }, [filteredEmployees]);

  // Get unique roles for filter dropdown
  const uniqueRoles = useMemo(() => {
    const roles = [...new Set(employees.map(e => e.role))].filter(Boolean);
    return roles.sort();
  }, [employees]);

  // Get unique projects across all employees
  const uniqueProjects = useMemo(() => {
    const set = new Set();
    employees.forEach(e => (e.projects || []).forEach(p => p && set.add(p)));
    return [...set].sort();
  }, [employees]);

  // Format raw team identifier to display name (e.g. mobile_app -> Mobile App)
  const formatTeamName = (raw) => {
    if (!raw) return 'Unassigned';
    return raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // Weighted sort: dev/QA teams first, then business, product, management last
  const teamSortKey = (raw) => {
    const t = (raw || '').toLowerCase();
    if (['management', 'management team', 'admin', 'leadership'].includes(t)) return 4;
    if (['product'].includes(t)) return 3;
    if (['business', 'business analyst', 'analyst'].includes(t)) return 2;
    return 0;
  };

  // Always group by team
  const groupedByTeam = useMemo(() => {
    const groups = {};
    filteredEmployees.forEach(emp => {
      const teamKey = emp.team || 'Unassigned';
      if (!groups[teamKey]) groups[teamKey] = [];
      groups[teamKey].push(emp);
    });
    return groups;
  }, [filteredEmployees]);

  const formatNumber = (num) => {
    if (!num) return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  if (isLoading) {
    return (
      <div style={{ padding: '4rem 2rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
        <CircularProgress size={44} thickness={4} sx={{ color: C.accent }} />
        <div style={{ fontSize: '1.05rem', fontWeight: 600, color: C.inkSoft }}>Loading team workload…</div>
        <div style={{ fontSize: '0.85rem', color: C.inkFaint }}>Fetching assignments and sprint data</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.1rem', color: C.danger, marginBottom: '0.5rem' }}>Failed to load team data</div>
        <div style={{ fontSize: '0.9rem', color: C.inkSoft }}>{error?.message || 'Unknown error'}</div>
        <div style={{ fontSize: '0.8rem', color: C.inkFaint, marginTop: '0.5rem' }}>{error?.response?.data?.message || ''}</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto', position: 'relative' }}>
      {isFetching && !isLoading && (
        <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1100, height: 3 }} />
      )}
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <FiUsers size={32} color={C.accent} />
            {teamPageCopy.title}
          </h1>
          <p style={{ color: C.inkSoft, fontSize: '1rem', margin: 0 }}>{teamPageCopy.subtitle}</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {canManageDocuments && (
            <button
              type="button"
              onClick={() => setDocumentsManageOpen(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 16px',
                background: '#fff',
                color: C.accentInk,
                border: `2px solid ${C.accentInk}`,
                borderRadius: 8,
                fontWeight: 700,
                fontSize: '0.85rem',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <FiBookOpen size={15} />
              Add Documents
            </button>
          )}
          {canGenerateWeeklyReport && (
            <button
              type="button"
              onClick={openWeeklyReportModal}
              disabled={weeklyReportBusy || isLoading}
              title={projectFilter === 'all' ? 'All projects' : `Project: ${projectFilter}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #1272B3 0%, #4338ca 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                fontSize: '0.85rem',
                cursor: weeklyReportBusy || isLoading ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 8px rgba(18, 114, 179,0.35)',
                whiteSpace: 'nowrap',
                opacity: weeklyReportBusy || isLoading ? 0.75 : 1,
              }}
            >
              <FiFileText size={15} />
              Weekly Report
            </button>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing || isLoading}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#fff', color: C.ink, border: `1px solid ${C.line}`, borderRadius: 8, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', whiteSpace: 'nowrap' }}>
            <FiRefreshCw size={14} style={{ transition: 'transform 0.5s', transform: refreshing ? 'rotate(360deg)' : 'rotate(0deg)' }} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {canSetupPerformanceReviewCycle(role) && <PerformanceReviewCycleMissingBanner />}

      {/* Team Summary Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
        gap: '1rem', 
        marginBottom: '2rem' 
      }}>
        <StatCard 
          icon={<FiUsers size={24} color={C.accent} />}
          label="Team Members"
          value={teamStats.total}
          subtext={`${teamStats.withActiveWork} with active work`}
          color={C.accent}
        />
        <StatCard 
          icon={<FiBriefcase size={24} color="#8b5cf6" />}
          label="Avg Tasks/Person"
          value={teamStats.avgTasksPerPerson}
          subtext="Active workload"
          color="#8b5cf6"
        />
        <StatCard 
          icon={<FiAlertCircle size={24} color={C.danger} />}
          label="Total Blockers"
          value={teamStats.totalBlockers}
          subtext={`${teamStats.overloaded} overloaded members`}
          color={C.danger}
        />
        <StatCard 
          icon={<FiCheckCircle size={24} color={C.done} />}
          label="Completed"
          value={teamStats.totalCompleted}
          subtext="Done + In Review"
          color={C.done}
        />
      </div>

      {/* Filters */}
      <div style={{ background: 'white', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 200 }}>
            <FiSearch color={C.inkSoft} size={18} />
            <input
              type="text"
              placeholder="Search by name, email, or team..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                padding: '0.5rem 0.75rem',
                border: `1px solid ${C.line}`,
                borderRadius: '6px',
                fontSize: '0.9rem',
                outline: 'none'
              }}
            />
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <FiFilter color={C.inkSoft} size={18} />
            <select
              value={projectFilter}
              onChange={(e) => { setProjectFilter(e.target.value); }}
              style={{
                padding: '0.5rem 0.75rem',
                border: `1px solid ${projectFilter !== 'all' ? C.accent : C.line}`,
                borderRadius: '6px',
                fontSize: '0.9rem',
                cursor: 'pointer',
                fontWeight: projectFilter !== 'all' ? 600 : 400,
                color: projectFilter !== 'all' ? C.accentInk : 'inherit',
              }}
            >
              <option value="all">All Projects</option>
              {uniqueProjects.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>

            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={{
                padding: '0.5rem 0.75rem',
                border: `1px solid ${C.line}`,
                borderRadius: '6px',
                fontSize: '0.9rem',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Roles</option>
              {uniqueRoles.map(role => (
                <option key={role} value={role}>{ROLE_DISPLAY[role] || role}</option>
              ))}
            </select>
            
            <select
              value={workloadFilter}
              onChange={(e) => setWorkloadFilter(e.target.value)}
              style={{
                padding: '0.5rem 0.75rem',
                border: `1px solid ${C.line}`,
                borderRadius: '6px',
                fontSize: '0.9rem',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Workloads</option>
              <option value="low">Light Load</option>
              <option value="medium">Moderate Load</option>
              <option value="high">Heavy Load</option>
            </select>
          </div>
        </div>
      </div>

      {/* Employee Cards Grid */}
      {filteredEmployees.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: C.inkFaint, background: 'white', borderRadius: '8px' }}>
          <FiUsers size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
          <div>No employees found matching the filters</div>
        </div>
      ) : (
        // Always group by team — management/admin teams sorted last
        Object.entries(groupedByTeam)
          .sort(([a], [b]) => {
            const diff = teamSortKey(a) - teamSortKey(b);
            return diff !== 0 ? diff : a.localeCompare(b);
          })
          .map(([teamKey, members]) => (
          <div key={teamKey} style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div style={{ width: 4, height: 22, background: C.accent, borderRadius: 4 }} />
              <span style={{ fontWeight: 700, fontSize: '1rem', color: C.ink }}>{formatTeamName(teamKey)}</span>
              <span style={{ background: C.accentSoft, color: C.accentInk, borderRadius: 12, padding: '2px 10px', fontSize: '0.75rem', fontWeight: 600 }}>
                {members.length} member{members.length !== 1 ? 's' : ''}
              </span>
              <div style={{ flex: 1, height: 1, background: C.line }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))', gap: '1rem' }}>
              {members.map(employee => (
                <EmployeeCard
                  key={employee.id}
                  employee={employee}
                  onClick={() => setProfileUser({ id: employee.id, name: employee.full_name || employee.email })}
                />
              ))}
            </div>
          </div>
        ))
      )}

      {/* Employee Profile Modal */}
      {profileUser && (
        <EmployeeProfileModal
          key={profileUser.id}
          userId={profileUser.id}
          userName={profileUser.name}
          onClose={() => setProfileUser(null)}
        />
      )}

      <TeamSopManageDialog open={documentsManageOpen} onClose={() => setDocumentsManageOpen(false)} />

      {weeklyReportOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="weekly-report-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2400,
            background: 'rgba(15,23,42,0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 14,
              width: '100%',
              maxWidth: 720,
              maxHeight: 'min(88vh, 900px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 25px 60px rgba(0,0,0,0.35)',
            }}
          >
            <div
              style={{
                padding: '1rem 1.25rem',
                borderBottom: `1px solid ${C.line}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <div>
                <h2 id="weekly-report-title" style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: C.ink }}>
                  Weekly report
                </h2>
                <div style={{ fontSize: '0.78rem', color: C.inkSoft, marginTop: 4, lineHeight: 1.45 }}>
                  {weeklyReportPhase === WR_RESULT && weeklyReportMeta ? (
                    <>
                      <span style={{ fontWeight: 600, color: C.ink }}>{weeklyReportMeta.projectScope}</span>
                      {' · '}
                      <span>
                        {weeklyReportMeta.summaryMode === 'teams'
                          ? 'Teams'
                          : weeklyReportMeta.summaryMode === 'individuals'
                            ? 'Individuals'
                            : 'Project'}{' '}
                        summary
                      </span>
                      {' · '}
                      <span>
                        {weeklyReportMeta.reportPeriod === 'last_week'
                          ? 'Last week only'
                          : weeklyReportMeta.reportPeriod === 'current_week'
                            ? 'Current week only'
                            : 'Last week + current week'}
                      </span>
                      {weeklyReportMeta.includeLastWeek && weeklyReportMeta.lastWeekRange ? (
                        <>
                          <br />
                          Last week: {weeklyReportMeta.lastWeekRange}
                        </>
                      ) : null}
                      {weeklyReportMeta.includeCurrentWeek && weeklyReportMeta.currentWeekRange ? (
                        <>
                          <br />
                          Current week: {weeklyReportMeta.currentWeekRange}
                        </>
                      ) : null}
                    </>
                  ) : (
                    <>
                      Scope uses the <strong>project filter</strong> on this page (
                      {projectFilter === 'all' ? 'all projects' : projectFilter}). Choose options below, then generate.
                    </>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {weeklyReportPhase === WR_RESULT && weeklyReportText && (
                  <button
                    type="button"
                    onClick={handleCopyWeeklyReport}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '7px 14px',
                      background: '#0f766e',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontWeight: 700,
                      fontSize: '0.82rem',
                      cursor: 'pointer',
                    }}
                  >
                    <FiCopy size={14} /> Copy
                  </button>
                )}
                {weeklyReportPhase === WR_RESULT && (
                  <button
                    type="button"
                    onClick={resetWeeklyReportContent}
                    style={{
                      padding: '7px 14px',
                      background: C.accentSoft,
                      border: '1px solid #a9ddf5',
                      borderRadius: 8,
                      fontWeight: 700,
                      fontSize: '0.82rem',
                      cursor: 'pointer',
                      color: C.accentInk,
                    }}
                  >
                    Change options
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setWeeklyReportOpen(false);
                    setWeeklyReportText('');
                    setWeeklyReportMeta(null);
                    setWeeklyReportLoading(false);
                    setWeeklyReportPhase(WR_CONFIGURE);
                  }}
                  style={{
                    padding: '6px 12px',
                    background: C.sunken,
                    border: `1px solid ${C.line}`,
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: '0.82rem',
                    cursor: 'pointer',
                    color: C.inkSoft,
                  }}
                >
                  Close
                </button>
              </div>
            </div>
            <div style={{ flex: 1, minHeight: 200, overflowY: 'auto', padding: '1rem 1.25rem' }}>
              {weeklyReportPhase === WR_GENERATING ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '3rem 1rem' }}>
                  <CircularProgress size={40} thickness={4} sx={{ color: '#4338ca' }} />
                  <div style={{ fontSize: '0.95rem', fontWeight: 600, color: C.inkSoft }}>Generating summary with AI…</div>
                  <div style={{ fontSize: '0.8rem', color: C.inkFaint, textAlign: 'center', maxWidth: 420 }}>
                    {weeklyReportPeriod === 'last_week' && 'Completions from last calendar week only. '}
                    {weeklyReportPeriod === 'current_week' && 'Active sprint work (in-flight) for the current window only. '}
                    {weeklyReportPeriod === 'both' && 'Last week’s completions and current sprint work. '}
                    {weeklySummaryMode === 'project' && 'Structure: project-wise. '}
                    {weeklySummaryMode === 'teams' && 'Structure: project → team → items. '}
                    {weeklySummaryMode === 'individuals' && 'Structure: project → assignee → items. '}
                    {projectFilter !== 'all' ? `Project: ${projectFilter}.` : 'All projects.'}
                  </div>
                </div>
              ) : weeklyReportPhase === WR_RESULT ? (
                <pre
                  style={{
                    margin: 0,
                    fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
                    fontSize: '0.84rem',
                    lineHeight: 1.55,
                    color: C.ink,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {weeklyReportText || 'No content.'}
                </pre>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 480 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: C.inkSoft, marginBottom: 6 }}>
                      Summary
                    </label>
                    <select
                      value={weeklySummaryMode}
                      onChange={(e) => setWeeklySummaryMode(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.55rem 0.75rem',
                        borderRadius: 8,
                        border: `1px solid ${C.line}`,
                        fontSize: '0.88rem',
                        background: C.sunken,
                        fontWeight: 500,
                        color: C.ink,
                      }}
                    >
                      <option value="project">Project — one section per project (default)</option>
                      <option value="teams">Teams — under each project, group by team (assignee team)</option>
                      <option value="individuals">Individuals — under each project, group by assignee</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: C.inkSoft, marginBottom: 6 }}>
                      Report period
                    </label>
                    <select
                      value={weeklyReportPeriod}
                      onChange={(e) => setWeeklyReportPeriod(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.55rem 0.75rem',
                        borderRadius: 8,
                        border: `1px solid ${C.line}`,
                        fontSize: '0.88rem',
                        background: C.sunken,
                        fontWeight: 500,
                        color: C.ink,
                      }}
                    >
                      <option value="last_week">Last week — completions only</option>
                      <option value="current_week">Current week — active sprint items only</option>
                      <option value="both">Both — last week + current / upcoming sprint focus</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={runWeeklyReportGeneration}
                    disabled={weeklyReportBusy || isLoading}
                    style={{
                      marginTop: 4,
                      padding: '10px 18px',
                      background: 'linear-gradient(135deg, #1272B3 0%, #4338ca 100%)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontWeight: 800,
                      fontSize: '0.9rem',
                      cursor: weeklyReportBusy || isLoading ? 'not-allowed' : 'pointer',
                      opacity: weeklyReportBusy || isLoading ? 0.7 : 1,
                      boxShadow: '0 2px 10px rgba(18, 114, 179,0.35)',
                    }}
                  >
                    Generate report
                  </button>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: C.inkFaint, lineHeight: 1.5 }}>
                    Counts and samples come from your issue database (same source as the sprint board). Epics are excluded from line lists; stories and tasks use story context when available.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon, label, value, subtext, color }) => (
  <div style={{ 
    background: 'white', 
    borderRadius: '12px', 
    padding: '1.25rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  }}>
    <div style={{ 
      padding: '0.75rem', 
      borderRadius: '10px', 
      background: `${color}15`
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: '0.85rem', color: C.inkSoft, marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontSize: '1.75rem', fontWeight: 700, color: C.ink }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: C.inkFaint }}>{subtext}</div>
    </div>
  </div>
);

// Employee Card Component
const EmployeeCard = ({ employee, onClick }) => {
  const workload = employee.workload || {};
  const workloadStyle = WORKLOAD_COLORS[workload.workload_level] || WORKLOAD_COLORS.low;
  
  return (
    <div 
      onClick={onClick}
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: '1.25rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: '1px solid transparent',
        hover: { boxShadow: '0 4px 12px rgba(0,0,0,0.12)', borderColor: C.line }
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
        e.currentTarget.style.borderColor = C.line;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
        e.currentTarget.style.borderColor = 'transparent';
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '1rem' }}>
        <div style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #2C8FC7 0%, #2C63AE 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 600,
          fontSize: '1.25rem',
          flexShrink: 0
        }}>
          {(employee.full_name || employee.email).charAt(0).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '1rem', color: C.ink, marginBottom: '0.25rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {employee.full_name || employee.email}
          </div>
          <div style={{ fontSize: '0.8rem', color: C.inkSoft, marginBottom: '0.25rem' }}>
            {ROLE_DISPLAY[employee.role] || employee.role}
          </div>
          <div style={{ 
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            padding: '0.25rem 0.5rem',
            borderRadius: '4px',
            background: workloadStyle.bg,
            color: workloadStyle.color,
            fontSize: '0.75rem',
            fontWeight: 600
          }}>
            <FiActivity size={12} />
            {workloadStyle.label} Load
          </div>
        </div>
      </div>

      {/* Workload Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100px, 100%), 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
        <div style={{ textAlign: 'center', padding: '0.5rem', background: C.sunken, borderRadius: '6px' }}>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: C.accentInk }}>{workload.open_tasks || 0}</div>
          <div style={{ fontSize: '0.7rem', color: C.inkSoft }}>Open</div>
        </div>
        <div style={{ textAlign: 'center', padding: '0.5rem', background: C.sunken, borderRadius: '6px' }}>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: workload.blockers > 0 ? C.danger : C.done }}>
            {workload.blockers || 0}
          </div>
          <div style={{ fontSize: '0.7rem', color: C.inkSoft }}>Blockers</div>
        </div>
        <div style={{ textAlign: 'center', padding: '0.5rem', background: C.sunken, borderRadius: '6px' }}>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: C.done }}>{workload.completed_tasks || 0}</div>
          <div style={{ fontSize: '0.7rem', color: C.inkSoft }}>Completed</div>
        </div>
      </div>

      {/* Current Sprint Info */}
      {employee.current_sprints && employee.current_sprints.length > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontSize: '0.75rem', color: C.inkSoft, marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <FiLayers size={12} />
            Active Sprint{employee.current_sprints.length > 1 ? 's' : ''}
          </div>
          <div style={{ fontSize: '0.85rem', color: C.ink, fontWeight: 500 }}>
            {employee.current_sprints[0].sprint_name}
            {employee.current_sprints.length > 1 && (
              <span style={{ color: C.inkSoft, fontWeight: 400 }}> +{employee.current_sprints.length - 1} more</span>
            )}
          </div>
          {employee.current_sprints[0].end_date && (
            <div style={{ fontSize: '0.75rem', color: C.inkFaint }}>
              Ends: {formatDate(employee.current_sprints[0].end_date)}
            </div>
          )}
        </div>
      )}

      {/* Recent Performance */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '1rem',
        paddingTop: '0.75rem',
        borderTop: `1px solid ${C.line}`
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <FiCheckCircle size={14} color={C.done} />
          <span style={{ fontSize: '0.8rem', color: C.inkSoft }}>
            {workload.completed_this_week || 0} this week
          </span>
        </div>
        {workload.avg_days_to_close && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <FiClock size={14} color={C.accent} />
            <span style={{ fontSize: '0.8rem', color: C.inkSoft }}>
              ~{workload.avg_days_to_close} days avg
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamActivity;
